/**
 * `ReadinessToolCard` — WP-10 (Simulation readiness ToolCard).
 *
 * The right-rail ToolCardDock variant of the simulation-readiness
 * surface. Activates when:
 *
 *  - `useViewerStore.sceneSelection.toolCardKind === 'readiness'`
 *    (per WP-2 selection contract), or
 *  - an `ai-candidate` / `readiness-gap` index node is selected.
 *
 * The card composes (in this order):
 *
 *  1. L0..L4 readiness chip header with non-color icon.
 *  2. Gap list **grouped by domain** (visualization / asset /
 *     simulation / operation) with per-group collapse toggle and
 *     numeric count, severity-icon row, mono identifier, short
 *     description, and a per-row "Open evidence" pivot button.
 *  3. **Trusted facts** vs **Candidate improvements** split with
 *     dedicated headers (PHASE-10 contract).
 *  4. **Export readiness graph** action — disabled today because the
 *     platform SDK does not yet expose
 *     `client.scene.exportReadinessGraph(layerId)`; surfaced with a
 *     `<Banner severity="info">` per the WP-10 blocker rule.
 *
 * Behavioral parity with the left-rail panel is enforced by sharing
 * `readinessModel.ts` helpers — no logic is duplicated.
 *
 * Spec references:
 *   - `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §7.10 + §8.10
 *   - `docs/results/PHASE-10-SIMULATION-READINESS-COMPLETION.md`
 *   - `geosoft-platform-design-system/docs/DESIGN_POLICY.md` §5
 */

import { useCallback, useMemo, useState } from "react";
import {
  Banner,
  Button,
  Chip,
  Icon,
  Stack,
  Text,
  tokens,
  type IconName,
} from "@/design";
import type {
  SimulationReadinessReport,
  UnifiedSceneTreeNode,
} from "@/app/scenePackage";
import { flattenSceneNodes } from "@/app/sceneExplorer";
import {
  dispatchToolCardRequest,
  selectByIndexNodeId,
  type SceneSelection,
} from "@/app/sceneSelection";
import {
  countReadinessFacts,
  firstEvidenceNodeId,
  groupReadinessGaps,
  hasExportReadinessGraph,
  readinessGapSeverityAppearance,
  readinessLevelAppearance,
  type ReadinessExportCapableClient,
  type ReadinessGapGroup,
  type ReadinessGapRow,
  type ReadinessGraphSnapshot,
} from "./readinessModel";

export interface ReadinessToolCardProps {
  readonly report: SimulationReadinessReport | null | undefined;
  readonly sceneTree?: UnifiedSceneTreeNode | null;
  /**
   * `LayerKind === 'gtwin'` callers may pass a fixed layer id when
   * the readiness card is bound to a single layer (e.g. opened from
   * the scene tree). Defaults to the report's `sceneId`.
   */
  readonly layerId?: string | null;
  /**
   * The gTwin SDK client. Feature-detected via
   * `hasExportReadinessGraph` — the card never imports the concrete
   * `GTwinClient` symbol so it stays decoupled from WP-1 sequencing.
   */
  readonly client?: ReadinessExportCapableClient | null;
  /**
   * Loading / error gating from the caller (TwinShell pipes through
   * `loadScenePackageRefs` warnings + record load state). Defaults
   * inferred from `report`.
   */
  readonly state?: ReadinessCardState;
  /**
   * Permission gate — when set the Export action is hidden and an
   * informational banner explains the read-only treatment.
   */
  readonly readOnly?: boolean;
  /**
   * Pivot handler. The card writes through
   * `dispatchToolCardRequest(viewer:tool-card-request)` and also
   * invokes this callback so the TwinShell can update the
   * scene-tree selection.
   */
  readonly onSelectNode?: (node: UnifiedSceneTreeNode) => void;
  /**
   * Optional injection point for the download action; lets tests
   * verify the SDK call without touching `URL.createObjectURL` or
   * the DOM. Defaults to a `Blob` download.
   */
  readonly onExport?: (snapshot: ReadinessGraphSnapshot) => void;
  /**
   * Injection point for the dispatch — defaults to the real
   * `dispatchToolCardRequest`. Tests override it to capture the
   * selection without touching `window`.
   */
  readonly onDispatchToolCardRequest?: (
    selection: SceneSelection,
  ) => void;
}

export type ReadinessCardState =
  | "ready"
  | "loading"
  | "empty"
  | "error"
  | "permission-denied"
  | "stale";

const STATE_BANNER: Partial<
  Record<
    ReadinessCardState,
    {
      readonly severity: "info" | "warning" | "error";
      readonly title: string;
      readonly body: string;
    }
  >
> = {
  loading: {
    severity: "info",
    title: "Loading readiness report",
    body: "Resolving simulation-readiness evidence from the scene index.",
  },
  empty: {
    severity: "info",
    title: "No readiness report",
    body: "The current gTwin has not produced a simulation-readiness report yet.",
  },
  error: {
    severity: "error",
    title: "Readiness report could not be loaded",
    body: "Trusted evidence cannot be displayed. Reload the project or contact the gTwin owner.",
  },
  "permission-denied": {
    severity: "error",
    title: "Permission denied",
    body: "Your role does not have access to readiness evidence for this gTwin.",
  },
  stale: {
    severity: "warning",
    title: "Readiness report is stale",
    body: "The displayed evidence may be older than the current scene-index revision.",
  },
};

export function ReadinessToolCard({
  report,
  sceneTree,
  layerId,
  client,
  state,
  readOnly,
  onSelectNode,
  onExport,
  onDispatchToolCardRequest,
}: ReadinessToolCardProps) {
  const effectiveState: ReadinessCardState =
    state ??
    (report === undefined
      ? "loading"
      : report === null
        ? "empty"
        : "ready");

  const banner = STATE_BANNER[effectiveState] ?? null;

  const nodesById = useMemo(
    () => new Map(flattenSceneNodes(sceneTree).map((node) => [node.id, node])),
    [sceneTree],
  );

  const reportForRender = report ?? null;
  const groups = useMemo(
    () => groupReadinessGaps(reportForRender),
    [reportForRender],
  );
  const levelAppearance = useMemo(
    () => readinessLevelAppearance(reportForRender?.currentReadinessLevel),
    [reportForRender],
  );
  const factCounts = useMemo(
    () => countReadinessFacts(reportForRender),
    [reportForRender],
  );

  const exportTargetLayerId =
    layerId ??
    reportForRender?.sceneId ??
    (typeof reportForRender?.runId === "string" ? reportForRender.runId : null);
  const exportSupported = hasExportReadinessGraph(client);
  const exportDisabled = !exportSupported || !exportTargetLayerId || readOnly;

  const handleExport = useCallback(async () => {
    if (!exportSupported || !exportTargetLayerId || !client) return;
    if (!hasExportReadinessGraph(client)) return;
    const snapshot = await client.scene.exportReadinessGraph(
      exportTargetLayerId,
    );
    if (onExport) {
      onExport(snapshot);
      return;
    }
    downloadReadinessGraphSnapshot(snapshot, exportTargetLayerId);
  }, [client, exportSupported, exportTargetLayerId, onExport]);

  const dispatchPivot = useCallback(
    (row: ReadinessGapRow, nodeId: string | null) => {
      const targetNodeId = nodeId ?? row.identifier;
      const selection = selectByIndexNodeId(targetNodeId, {
        nodeKind: nodeId ? "readiness-gap" : "readiness-gap",
        source: "tool-card",
      });
      const dispatcher = onDispatchToolCardRequest ?? dispatchToolCardRequest;
      dispatcher(selection);
      if (nodeId) {
        const targetNode = nodesById.get(nodeId);
        if (targetNode && onSelectNode) onSelectNode(targetNode);
      }
    },
    [nodesById, onDispatchToolCardRequest, onSelectNode],
  );

  return (
    <section
      aria-label="Simulation readiness tool card"
      data-testid="readiness-tool-card"
      style={containerStyle}
    >
      <Stack direction="row" align="center" justify="space-between" gap={2}>
        <Stack direction="row" align="center" gap={2}>
          <Icon name="fact_check" size={24} decorative />
          <div>
            <Text variant="subtitle" as="h2" style={{ margin: 0 }}>
              Simulation readiness
            </Text>
            <Text variant="caption" as="p" style={{ margin: 0 }}>
              {reportForRender?.domain ?? "domain pending"}
            </Text>
          </div>
        </Stack>
        <Chip
          icon={levelAppearance.icon}
          label={levelAppearance.label}
          variant={levelAppearance.variant}
          aria-label={`Readiness level ${levelAppearance.level}: ${levelAppearance.label}`}
        />
      </Stack>

      {banner ? (
        <div style={{ marginTop: tokens.space[2] }}>
          <Banner
            severity={banner.severity}
            title={banner.title}
            body={banner.body}
            narrationKey={`readiness:${effectiveState}`}
          />
        </div>
      ) : null}

      <SectionHeading title="Domain checklist" />
      {groups.length > 0 ? (
        <Stack direction="column" gap={2}>
          {groups.map((group) => (
            <DomainGroup
              key={group.domain}
              group={group}
              onOpenEvidence={(row) => {
                const nodeId = firstEvidenceNodeId(row.rawEvidence);
                dispatchPivot(row, nodeId);
              }}
            />
          ))}
        </Stack>
      ) : (
        <EmptyText>No outstanding readiness gaps are reported.</EmptyText>
      )}

      <SectionHeading title="Trusted facts" />
      <TrustedFactsSection
        report={reportForRender}
        trustedCount={factCounts.trusted}
      />

      <SectionHeading title="Candidate improvements" />
      <CandidateImprovementsSection
        report={reportForRender}
        candidateCount={factCounts.candidate}
        nodesById={nodesById}
        onSelectNode={onSelectNode}
      />

      <SectionHeading title="Export" />
      <ExportRow
        disabled={Boolean(exportDisabled)}
        supported={exportSupported}
        readOnly={Boolean(readOnly)}
        onExport={handleExport}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Grouped gap display
// ---------------------------------------------------------------------------

function DomainGroup({
  group,
  onOpenEvidence,
}: {
  readonly group: ReadinessGapGroup;
  readonly onOpenEvidence: (row: ReadinessGapRow) => void;
}) {
  const [open, setOpen] = useState<boolean>(true);
  const headingId = `readiness-group-${group.domain}`;
  const regionId = `${headingId}-rows`;
  return (
    <div style={groupStyle}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={regionId}
        onClick={() => setOpen((current) => !current)}
        style={groupHeaderStyle}
      >
        <Icon name={group.appearance.icon} size={20} decorative />
        <span
          id={headingId}
          style={{
            flex: "1 1 auto",
            color: tokens.color.ink.primary,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.size.sm,
            fontWeight: tokens.font.weight.medium,
            textAlign: "left",
          }}
        >
          {group.appearance.label}
        </span>
        <span style={monoCountStyle} aria-label={`${group.rows.length} gaps`}>
          {group.rows.length}
        </span>
        <Icon
          name={open ? "keyboard_arrow_up" : "chevron_right"}
          size={20}
          decorative
        />
      </button>
      {open ? (
        <ul role="list" id={regionId} aria-labelledby={headingId} style={listStyle}>
          {group.rows.map((row) => (
            <GapRowView
              key={row.id}
              row={row}
              onOpenEvidence={() => onOpenEvidence(row)}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function GapRowView({
  row,
  onOpenEvidence,
}: {
  readonly row: ReadinessGapRow;
  readonly onOpenEvidence: () => void;
}) {
  const severity = readinessGapSeverityAppearance(row.severity);
  return (
    <li style={rowItemStyle}>
      <Stack direction="row" align="flex-start" gap={2}>
        <Icon name={severity.icon} size={20} decorative />
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <Stack direction="row" align="center" gap={2}>
            <strong
              style={{
                color: tokens.color.ink.primary,
                fontSize: tokens.font.size.sm,
              }}
            >
              {row.title}
            </strong>
            <span style={monoIdentifierStyle} aria-label={`Identifier ${row.identifier}`}>
              ({row.identifier})
            </span>
          </Stack>
          <span
            style={{
              display: "block",
              marginTop: tokens.space[1],
              color: tokens.color.ink.secondary,
              fontSize: tokens.font.size.xs,
            }}
          >
            {row.description}
          </span>
          <Chip
            icon={severity.icon}
            label={severity.severity}
            variant={severity.variant}
          />
        </div>
      </Stack>
      <Stack direction="row" gap={2} style={{ marginTop: tokens.space[2] }}>
        <Button
          variant="secondary"
          onClick={onOpenEvidence}
          aria-label={`Open evidence for ${row.title}`}
        >
          Open evidence
        </Button>
      </Stack>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Trusted facts / candidate improvements
// ---------------------------------------------------------------------------

function TrustedFactsSection({
  report,
  trustedCount,
}: {
  readonly report: SimulationReadinessReport | null;
  readonly trustedCount: number;
}) {
  if (!report || trustedCount === 0) {
    return (
      <Stack direction="row" gap={2} align="center">
        <Chip
          icon="verified"
          label={`${trustedCount} trusted facts`}
          variant="neutral"
        />
        <EmptyText>No trusted facts are currently certifying readiness.</EmptyText>
      </Stack>
    );
  }
  const trustedFacts = (report.trustedFactsUsed ?? []) as readonly Record<
    string,
    unknown
  >[];
  return (
    <Stack direction="column" gap={2}>
      <Chip
        icon="verified"
        label={`${trustedCount} trusted facts`}
        variant="success"
      />
      <ul role="list" style={listStyle}>
        {trustedFacts.slice(0, 5).map((fact, index) => (
          <li key={`trusted:${index}`} style={rowItemStyle}>
            <Stack direction="row" align="flex-start" gap={2}>
              <Icon name="verified" size={20} decorative />
              <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                <strong
                  style={{
                    color: tokens.color.ink.primary,
                    fontSize: tokens.font.size.sm,
                  }}
                >
                  {readFactLabel(fact)}
                </strong>
                <span
                  style={{
                    display: "block",
                    marginTop: tokens.space[1],
                    color: tokens.color.ink.secondary,
                    fontSize: tokens.font.size.xs,
                  }}
                >
                  {readFactSource(fact)}
                </span>
              </div>
            </Stack>
          </li>
        ))}
      </ul>
    </Stack>
  );
}

function CandidateImprovementsSection({
  report,
  candidateCount,
  nodesById,
  onSelectNode,
}: {
  readonly report: SimulationReadinessReport | null;
  readonly candidateCount: number;
  readonly nodesById: ReadonlyMap<string, UnifiedSceneTreeNode>;
  readonly onSelectNode?: (node: UnifiedSceneTreeNode) => void;
}) {
  if (!report || candidateCount === 0) {
    return (
      <Stack direction="row" gap={2} align="center">
        <Chip
          icon="pending"
          label={`${candidateCount} candidate improvements`}
          variant="neutral"
        />
        <EmptyText>No AI candidate facts are currently improving readiness.</EmptyText>
      </Stack>
    );
  }
  const candidates = report.candidateFactsThatCouldImproveReadiness ?? [];
  return (
    <Stack direction="column" gap={2}>
      <Chip
        icon="pending"
        label={`${candidateCount} candidate improvements`}
        variant="warning"
      />
      <ul role="list" style={listStyle}>
        {candidates.slice(0, 5).map((item, index) => {
          const evidence =
            (item.candidateEvidence ?? []) as readonly Record<string, unknown>[];
          const nodeId = firstEvidenceNodeId(evidence);
          const node = nodeId ? (nodesById.get(nodeId) ?? null) : null;
          return (
            <li
              key={`${item.requirement ?? "candidate"}:${index}`}
              style={rowItemStyle}
            >
              <Stack direction="row" align="flex-start" gap={2}>
                <Icon name="psychology" size={20} decorative />
                <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                  <strong
                    style={{
                      color: tokens.color.ink.primary,
                      fontSize: tokens.font.size.sm,
                    }}
                  >
                    {item.label ??
                      item.requirement ??
                      "Candidate readiness improvement"}
                  </strong>
                  <span
                    style={{
                      display: "block",
                      marginTop: tokens.space[1],
                      color: tokens.color.ink.secondary,
                      fontSize: tokens.font.size.xs,
                    }}
                  >
                    {item.message ??
                      "Candidate evidence is awaiting approval before promotion."}
                  </span>
                </div>
              </Stack>
              {node && onSelectNode ? (
                <Stack direction="row" gap={2} style={{ marginTop: tokens.space[2] }}>
                  <Button
                    variant="ghost"
                    onClick={() => onSelectNode(node)}
                    aria-label={`Open linked node for ${item.label ?? item.requirement ?? "candidate"}`}
                  >
                    Open linked node
                  </Button>
                </Stack>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Export row
// ---------------------------------------------------------------------------

function ExportRow({
  disabled,
  supported,
  readOnly,
  onExport,
}: {
  readonly disabled: boolean;
  readonly supported: boolean;
  readonly readOnly: boolean;
  readonly onExport: () => void;
}) {
  return (
    <Stack direction="column" gap={2}>
      <Stack direction="row" gap={2}>
        <Button
          variant="primary"
          disabled={disabled}
          onClick={onExport}
          aria-label="Export readiness graph snapshot"
          data-testid="readiness-export-button"
        >
          <Stack direction="row" align="center" gap={1}>
            <Icon name="download" size={20} decorative />
            <span>Export readiness graph</span>
          </Stack>
        </Button>
      </Stack>
      {!supported ? (
        <Banner
          severity="info"
          title="Export coming soon"
          body="Coming with platform SDK readiness export endpoint."
          narrationKey="readiness:export-unsupported"
        />
      ) : null}
      {supported && readOnly ? (
        <Banner
          severity="info"
          title="Read-only project"
          body="Your role does not have permission to export this readiness graph."
          narrationKey="readiness:export-readonly"
        />
      ) : null}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

function SectionHeading({ title }: { readonly title: string }) {
  return (
    <h3
      style={{
        margin: `${tokens.space[3]}px 0 ${tokens.space[2]}px`,
        fontSize: tokens.font.size.sm,
        color: tokens.color.ink.primary,
      }}
    >
      {title}
    </h3>
  );
}

function EmptyText({ children }: { readonly children: React.ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        color: tokens.color.ink.secondary,
        fontSize: tokens.font.size.sm,
      }}
    >
      {children}
    </p>
  );
}

function readFactLabel(fact: Record<string, unknown>): string {
  if (typeof fact.label === "string") return fact.label;
  if (typeof fact.requirement === "string") return fact.requirement;
  if (typeof fact.predicate === "string") return fact.predicate;
  if (typeof fact.kind === "string") return fact.kind;
  return "Trusted fact";
}

function readFactSource(fact: Record<string, unknown>): string {
  if (typeof fact.source === "string") return `Source: ${fact.source}`;
  if (typeof fact.nodeId === "string") return `Node: ${fact.nodeId}`;
  if (typeof fact.subjectNodeId === "string") return `Node: ${fact.subjectNodeId}`;
  return "Source: trusted catalog";
}

function downloadReadinessGraphSnapshot(
  snapshot: ReadinessGraphSnapshot,
  layerId: string,
): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  try {
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `readiness-graph-${layerId}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  } catch {
    // Swallow — the export action is best-effort; UI surface is
    // already gated by the disabled state. Errors do not throw to
    // the caller.
  }
}

// ---------------------------------------------------------------------------
// Style constants (all tokenized; no raw hex, rgba, or px literals)
// ---------------------------------------------------------------------------

const containerStyle = {
  padding: tokens.space[3],
  background: tokens.color.surface[1],
  border: tokens.border.thin,
  borderRadius: tokens.radius.lg,
  color: tokens.color.ink.primary,
  display: "flex",
  flexDirection: "column" as const,
  gap: tokens.space[2],
};

const groupStyle = {
  border: tokens.border.thin,
  borderRadius: tokens.radius.md,
  background: tokens.color.surface[0],
};

const groupHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: tokens.space[2],
  width: "100%",
  minHeight: tokens.density.touchMin,
  padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
  background: tokens.color.surface[2],
  color: tokens.color.ink.primary,
  border: 0,
  borderTopLeftRadius: tokens.radius.md,
  borderTopRightRadius: tokens.radius.md,
  cursor: "pointer",
};

const monoCountStyle = {
  fontFamily: tokens.font.family.mono,
  fontSize: tokens.font.role.engMeta.size,
  color: tokens.color.ink.secondary,
};

const monoIdentifierStyle = {
  fontFamily: tokens.font.family.mono,
  fontSize: tokens.font.role.engMeta.size,
  color: tokens.color.ink.muted,
};

const listStyle = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column" as const,
  gap: tokens.space[1],
};

const rowItemStyle = {
  border: tokens.border.thin,
  borderRadius: tokens.radius.md,
  background: tokens.color.surface[0],
  padding: tokens.space[2],
};

// Re-export shared icon type so callers don't need a second import.
export type { IconName };
